import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "../../../../../scss/components/_ag-table.scss";
import { getServerSideDatasourcePPC } from "@revbox/helpers-fe";
import "ag-grid-charts-enterprise";
import {
  PositionRenderer,
  PositionCellTooltipRenderer,
  PositionColumnHeaderTooltipRenderer,
} from "./SpecificPositionRenderer";
import { StatusCellTooltipRenderer, StatusColumnHeaderTooltipRenderer, StatusRenderer } from "./SpecificStatusRenderer";
import { ActionCellColumnHeaderTooltipRenderer, ActionCellTooltipRenderer, ActionRenderer } from "./SpecificActionRenderer";
import {
  MissedClicksForecastCellColumnHeaderTooltipRenderer,
  MissedClicksForecastCellTooltipRenderer,
  MissedClicksForecastRenderer,
} from "./SpecificMissedClicksForecastRenderer";
import { NameRenderer, NameCellTooltipRenderer, NameColumnHeaderTooltipRenderer } from "./SpecificNameRenderer";
import {
  YourBidCellColumnHeaderTooltipRenderer,
  YourBidCellRenderer,
  YourBidCellTooltipRenderer,
} from "./SpecificYourBidRenderer";
import { TopBidCellColumnHeaderTooltipRenderer, TopBidCellRenderer, TopBidCellTooltipRenderer } from "./SpecificTopBidRenderer";
import { YourClicksCellColumnHeaderTooltipRenderer, YourClicksCellTooltipRenderer, ClicksCellRenderer } from "./SpecificClicksRenderer";
import { CustomTooltip } from "../../common/CustomTooltip";
import { Card } from "react-bootstrap";
import { TaxonomyModule } from "../../../../../modules/taxonomy";
import { specificPPCAuctionsLabels, statusLabels, searchPlaceholder, advIntervalAuctionsBreadcrumbs } from "@revbox/localization";
import { useCookiesContext } from "../../../../../contexts/CookiesContext";
import IntervalAuctionCellRenderer from "./IntervalAuctionCellRenderer";

const getCustomColumns = (language) => [
  {
    field: "Name",
    cellRenderer: NameRenderer,
    headerTooltip: NameColumnHeaderTooltipRenderer(),
    tooltipValueGetter: NameCellTooltipRenderer
  },
  {
    field: "Position",
    cellRenderer: PositionRenderer,
    headerTooltip: PositionColumnHeaderTooltipRenderer(),
    tooltipValueGetter: PositionCellTooltipRenderer,
  },
  {
    field: "Status",
    cellRenderer: StatusRenderer,
    headerTooltip: StatusColumnHeaderTooltipRenderer(),
    tooltipValueGetter: StatusCellTooltipRenderer,
  },
  {
    field: "Your Bid",
    cellRenderer: YourBidCellRenderer,
    headerTooltip: YourBidCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: YourBidCellTooltipRenderer
  },
  {
    field: "Top Bid",
    cellRenderer: TopBidCellRenderer,
    headerTooltip: TopBidCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: TopBidCellTooltipRenderer
  },
  {
    field: "Your Clicks",
    cellRenderer: ClicksCellRenderer,
    headerTooltip: YourClicksCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: YourClicksCellTooltipRenderer,
  },
  {
    field: "Action",
    cellRenderer: ActionRenderer,
    headerTooltip: ActionCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: ActionCellTooltipRenderer
  },
  {
    field: "Missed Clicks Forecast",
    cellRenderer: MissedClicksForecastRenderer,
    headerTooltip: MissedClicksForecastCellColumnHeaderTooltipRenderer(),
    tooltipValueGetter: MissedClicksForecastCellTooltipRenderer,
  },
  {
    field: "Auction Close",
    cellRenderer: ({value}) => IntervalAuctionCellRenderer({value, language}),
    headerTooltip: "Auction Close Time",
    tooltipValueGetter: ({value}) =>  {
      const date = new Date(value)
      if (isNaN(date.getTime())) {
        return value
      }

      if (new Date().getTime() > date.getTime()) {
        return advIntervalAuctionsBreadcrumbs[language].closedAuctionTitle
      }

      return date.toLocaleString()}
  },
  {
    field: "Forecasting",
    cellRenderer: () => {},
    headerTooltip: () => {},
    tooltipValueGetter: () => {},
  }
];

const defaultColDef = {
  flex: 1,
  minWidth: 90,
  tooltipComponent: CustomTooltip,
}

export const SpecificPPCAuctions = ({
  getData,
  skin,
  onClick,
  campaignId,
  updatePage,
  isHTR,
  display,
  setAnyActiveSpecificBid,
  setAnyPPCAuctions,
  auctionType
}) => {
  const { getLanguageAbbr } = useCookiesContext();
  const languageAbbr = getLanguageAbbr();
  const multilang = specificPPCAuctionsLabels[languageAbbr];
  const search_placeholder = searchPlaceholder[languageAbbr];
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ minHeight: "100%", width: "100%" }), []);
  const customColumns = getCustomColumns(languageAbbr);

  const [search, setSearch] = useState("");
  const [gridApi, setGridApi] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);

  const [data, setData] = useState([]);

  useEffect(() => {
    getData({search}, campaignId).then((response) => {
      if (response.success) {
        const anyActiveBidFlag = response.data.rows.some(row => {
          const status = row[2].split("#")[0];
          const translatedStatuses = statusLabels[languageAbbr];
          const cases = [translatedStatuses["Live"], translatedStatuses["Paused"], translatedStatuses["Outbid"]]
          return cases.includes(status)
        });
        setAnyActiveSpecificBid(anyActiveBidFlag);
        setAnyPPCAuctions(response.data.rows.length > 0);
        setData(response.data.rows);
      }
    });
    // updatePage is used to refresh the tabs when the page is updated
  }, [campaignId, updatePage]);


  const onFilterTextBoxChanged = (e) => {
    setSearch(e.target.value);
  };

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);

    const datasource = getServerSideDatasourcePPC(
      getData,
      setColumnDefs,
      search,
      customColumns,
      campaignId,
      languageAbbr
    );

    setTimeout(() => {
      params.api.setGridOption("serverSideDatasource", datasource);
    }, 2000);
  }, [campaignId]);

  useEffect(() => {
    if (gridApi) {
      const datasource = getServerSideDatasourcePPC(
        getData,
        setColumnDefs,
        search,
        customColumns,
        campaignId,
        languageAbbr
      );

      setTimeout(() => {
        gridApi.setGridOption("serverSideDatasource", datasource);
      }, 2000);
    }
  }, [gridApi, search, updatePage, campaignId]);

  if (auctionType !== "interval" && (display !== "specific" || data?.length === 0)) {
    return <></>;
  }
  
  return (
    <Card className="card-one">
      <Card.Header className="d-flex flex-column align-items-start">
        <Card.Title as="h6" className="mb-2">
          {isHTR && multilang.htrTitle}
          {!isHTR && multilang.title}
        </Card.Title>
        { isHTR &&
          <Card.Subtitle as="p">
            {multilang.subtitle}
            <Card.Link
              target="_blank"
              className="ps-2"
              href="https://help.hoteltechreport.com/en/collections/10828721-auction-types"
            >
              {multilang.learnMore}
            </Card.Link>
          </Card.Subtitle>
        }
      </Card.Header>
      <Card.Body>
        <div style={containerStyle}>
          <div
            style={gridStyle}
            className={skin === "dark" ? "ag-theme-quartz-dark ag-theme-quartz" : "ag-theme-quartz-light ag-theme-quartz"}
          >
            <div style={{ marginBottom: "10px" }}>
              <input
                type="text"
                className="input-search"
                placeholder={search_placeholder}
                onInput={onFilterTextBoxChanged}
              />
            </div>
            <AgGridReact
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              tooltipShowDelay={500}
              tooltipHideDelay={200000}
              paginationPageSize={10}
              paginationPageSizeSelector={false}
              cacheBlockSize={10}
              rowModelType={"serverSide"}
              domLayout={"autoHeight"}
              suppressContextMenu={true}
              onCellClicked={onClick}
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
